<template>
  <v-dialog v-model="dialog" max-width="90%" no-click-animation :persistent="loading">
    <v-card>
      <v-card-title class="headline">Bulk Approve Trips</v-card-title>

      <v-container fluid class="px-8">
        <div>
          <v-col cols="12" class="d-flex flex-column justify-center align-center" v-if="loading">
            <v-progress-linear color="primary" indeterminate class="w-full"></v-progress-linear>
          </v-col>
          <div class="d-flex justify-space-between align-center mb-4 ml-1" v-if="!loading">
            <v-checkbox
              hide-details="auto"
              :input-value="selectedTrips?.length > 0"
              @change="toggleAllTripSelection"
              :label="`Selected Trips (max 50): ${selectedTrips?.length}`"
            />
            <v-btn :disabled="selectedTrips?.length === 0" class="ml-4 primary" @click="handleApproveSelectedTrips">
              Approve Selected
            </v-btn>
          </div>

          <v-col v-if="paginatedTripRequests.length === 0" class="text-center">No available trips to approve</v-col>
          <trip-request
            :key="tr.id"
            :tripRequest="tr"
            :hide-quick-actions="true"
            :disable-open-trip-request="true"
            :limit-selected-trips="true"
            class="card-spacing"
            v-for="tr of paginatedTripRequests"
          ></trip-request>
        </div>

        <v-toolbar height="50" elevation="4" rounded class="my-4 d-flex flex-row justify-center">
          <v-pagination
            :length="totalPages"
            :totalVisible="10"
            @input="$refs.bridgeLayout.resetScroll()"
            v-model="currentPage"
          ></v-pagination
        ></v-toolbar>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { GET_TRIP_REQUESTS } from '@/store/modules/TripRequest/actions';
import TripRequest from '@/components/TripRequest.vue';
import { TRIP_STATUS } from '@/shared/common';

export default {
  name: 'BulkApproveTrips',
  inject: ['eventHub'],
  components: {
    TripRequest,
  },
  props: {
    sortedTripRequests: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      dialog: false,
      currentPage: 1,
      filteredTripRequests: [],
      loading: false,
      perPage: 20,
    };
  },
  computed: {
    ...mapGetters('tripRequest', ['selectedTrips', 'tripRequestsById', 'tripRequests', 'showApproveLevel']),
    totalPages() {
      return Math.ceil(this.filteredTripRequests.length / this.perPage);
    },
    paginatedTripRequests() {
      return this.filteredTripRequests.slice().splice((this.currentPage - 1) * this.perPage, this.perPage);
    },
  },
  async mounted() {
    this.fetchPendingItems();
  },
  methods: {
    ...mapActions('tripRequest', [
      GET_TRIP_REQUESTS,
      'toggleTripSelected',
      'clearSelectedTrips',
      'approveBulkTripRequest',
    ]),
    toggleAllTripSelection(value) {
      if (!value) {
        this.clearSelectedTrips();
        return;
      }

      const ids = this.filteredTripRequests.slice(0, 50).map((e) => e.id); // limit to 50 only to avoid performance issues
      this.toggleTripSelected({ tripRequestId: ids });
    },
    async fetchPendingItems() {
      this.filteredTripRequests =
        this.sortedTripRequests.length > 0
          ? this.sortedTripRequests.filter((e) => this.showApproveLevel(e))
          : this.tripRequests.filter((e) => this.showApproveLevel(e));
    },

    isPendingApproval(tripRequest) {
      const isPendingStatus = tripRequest.status === TRIP_STATUS.SUBMITTED;
      const hasPendingApproval = Boolean(tripRequest?.approval?.awaitingApproval?.id);
      return isPendingStatus && hasPendingApproval;
    },
    async handleApproveSelectedTrips() {
      const yes = await this.$myconfirm('Are you sure you want to bulk approve these trips?');
      if (yes) {
        this.loading = true;
        try {
          await this.approveBulkTrips();
        } catch (error) {
          console.error(error);
        } finally {
          this.loading = false;
          this.close();
        }
      }
    },
    async approveBulkTrips() {
      const tripRequestIds = this.selectedTrips;
      if (!tripRequestIds.length) {
        this.$myalert.error('No trips selected. Please select trips to approve.', true);
        return;
      }

      try {
        const payload = tripRequestIds.reduce((acc, tripRequestId) => {
          const tripRequest = this.tripRequestsById[tripRequestId];
          if (!tripRequest) return acc;

          const { awaitingApproval } = tripRequest.approval;
          if (!awaitingApproval || !awaitingApproval.id) return acc;

          acc.push({
            tripRequestId: tripRequest.id,
            approvalLevelId: awaitingApproval.id,
            tripsApprovalId: awaitingApproval.tripsApprovalId,
            fundingSourceId: awaitingApproval.fundingSourceId || undefined,
            approved: 1,
          });

          return acc;
        }, []);

        const response = await this.approveBulkTripRequest(payload);

        if (response.results) {
          const successes = response.results.filter((res) => res.status === 'fulfilled' && res.value?.success === true);
          const failures = response.results.filter((res) => res?.value?.id === -1);

          if (successes.length === response.results.length) {
            this.$myalert.success('Selected trips have been successfully approved.');
          } else if (failures.length === response.results.length) {
            const errorMessages = failures.slice(0, 5).map((failure) => {
              const errorCodes = failure.value.error.map((e) => e?.message).join(', ');
              return `TRIP ID #${failure.value.tripRequestId} ERROR: ${errorCodes}`;
            });

            if (failures.length > 5) errorMessages.push(`PLUS ${failures.length - 5} MORE ERRORS`);

            const errorMessagesString = errorMessages.join('\n');

            this.$myalert.error(errorMessagesString);
          } else {
            const successMessage = 'SOME TRIPS HAVE BEEN SUCCESSFULLY APPROVED.';

            const errorMessages = failures.slice(0, 5).map((failure) => {
              const errorCodes = failure.value.error.map((e) => e?.message).join(', ');
              return `TRIP ID #${failure.value.tripRequestId} ERROR: ${errorCodes}`;
            });

            if (failures.length > 5) errorMessages.push(`PLUS ${failures.length - 5} MORE ERRORS`);

            const errorMessagesString = errorMessages.join('\n');

            this.$myalert.error(`${successMessage}\n${errorMessagesString}`);
          }
        }
      } catch (e) {
        this.$myalert.error(e.message || 'An error occurred while approving selected trips.');
      }
    },
    close() {
      this.clearSelectedTrips();
      this.$emit('refreshTripRequests', false);
      this.dialog = false;
    },
  },
  watch: {
    dialog() {
      this.fetchPendingItems();
      this.clearSelectedTrips();
    },
  },
};
</script>

<style lang="scss" scoped>
.right {
  float: right;
}
.button-chip {
  height: 36px !important;
  font-size: 16px !important;
}
.v-input__slot {
  margin-bottom: 0px !important;
  margin-top: 2px !important;
}
.row + .row {
  margin-top: 0px;
}
.card-spacing {
  margin-bottom: 10px !important;
  padding: 0 5px;
}
.w-full {
  width: 100%;
}
</style>
